// Ported from files/snapdraw-design-kit/code/src/lib/i18n/index.tsx
// (Lovable reference implementation) — the provider/hooks are pure
// presentation-layer plumbing.
//
// Backlog UX-6 (2026-08-26): the language choice now persists to a real
// `profiles.language` column (migration 0017), not just localStorage —
// tied to the account, not the browser. localStorage stays in the picture
// as a same-device cache only, for two reasons: (1) it renders the correct
// language instantly on repeat visits, before the DB round-trip resolves,
// avoiding a flash of English; (2) it's still the only thing available on
// public pages (login/signup) before there's a session to read a profile
// from. On mount, the DB value (once fetched) always wins over whatever's
// cached locally — simpler than reconciling which one is "more correct,"
// and the cost of getting it wrong once (an existing tester's pre-UX-6
// localStorage-only pick resetting to the account's still-default 'en') is
// a single click to fix, not worth extra logic for at this scale.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { dictionaries, type Dictionary } from "./strings";
import { getProfile, updateProfile } from "@/lib/profile";

export type Language = "en" | "es";

export const LANGUAGES: { code: Language; label: string; native: string }[] = [
  { code: "en", label: "EN", native: "English" },
  { code: "es", label: "ES", native: "Español" },
];

/** Same-device cache only now — see the file header comment. The account's own `profiles.language` (via getProfile()/updateProfile()) is the real source of truth once a session exists. */
const STORAGE_KEY = "snapdraw.language";

function readStoredLanguage(): Language {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === "es" ? "es" : "en";
}

type Ctx = {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
};

const I18nContext = createContext<Ctx | null>(null);

function lookup(dict: Dictionary, key: string): string | undefined {
  return dict[key];
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>("en");

  useEffect(() => {
    const stored = readStoredLanguage();
    if (stored !== "en") setLangState(stored);
    // Reconcile with the account's real preference once a session exists —
    // fails silently (caught, no-op) on public/unauthenticated pages, same
    // as IdentityProvider's getProfile() call.
    getProfile()
      .then((p) => {
        if (p?.language && p.language !== stored) {
          setLangState(p.language);
          if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, p.language);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Language) => {
    setLangState(next);
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, next);
    // Fire-and-forget: persists for signed-in users, silently no-ops on
    // public pages (updateProfile throws "Not signed in") — the local
    // state/cache above already gives instant, offline-safe UI either way.
    updateProfile({ language: next }).catch(() => {});
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      const value = lookup(dictionaries[lang], key) ?? lookup(dictionaries.en, key) ?? key;
      if (!vars) return value;
      return value.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m));
    },
    [lang],
  );

  const value = useMemo<Ctx>(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): Ctx {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <LanguageProvider>");
  return ctx;
}

/** Convenience hook when only the translate fn is needed. */
export function useT() {
  return useI18n().t;
}
